import { SNOW_SURFACE_GLSL } from "./snow-presentation.js";
import type { BiomeId } from "@endclock/shared";
import type { Camera } from "./camera.js";
import { composeBiomeEnvironment, legacyEnvironmentFogProfile, type EnvironmentPresentation } from "./environment-presentation.js";
import { LocalShadowCamera, LocalShadowSelection, createLocalShadow, destroyLocalShadow, localShadowSignature, type LocalShadowResource } from "./local-shadows.js";
import { mat4Identity, type Mat4 } from "./math.js";
import type { MeshGeometry } from "./mesh.js";
import { renderResolution } from "./render-resolution.js";
import { WATER_VISUAL_CONFIG, WaterInteractionEffects, type WaterVisualLocomotion } from "./water.js";
import { WATER_VERTEX_SOURCE, WATER_FRAGMENT_SOURCE, WATER_SPLASH_VERTEX_SOURCE, WATER_SPLASH_FRAGMENT_SOURCE } from "./water-shaders.js";
import {
  LIGHTING_CONFIG, LIGHTING_GLSL, DirectionalShadowCamera, LocalLightSelection, createLightingUniforms,
  type LightingUniforms, type LocalLight,
} from "./lighting.js";
import {
  ATMOSPHERE_CONFIG,
  BIOME_ATMOSPHERE_PROFILES,
  DEFAULT_ATMOSPHERE_FLAGS,
  TPS_SKY_COLOR_GLSL,
  ENVIRONMENT_SKY_UNIFORM_NAMES,
  blendBiomeAtmosphere,
  createAtmosphereParticleData,
  particleCountForQuality,
  tpsFogProfile,
  type AtmosphereFeatureFlags,
  type AtmosphereFogProfile,
  type AtmosphereQuality,
  type BiomeAtmosphereProfile,
} from "./atmosphere.js";

export interface RendererMetrics {
  fps: number;
  frameMs: number;
  cpuMs: number;
  drawCalls: number;
  triangles: number;
  totalTerrainTriangles: number;
  visibleTerrainChunks: number;
  totalTerrainChunks: number;
  waterDrawCalls: number;
  waterTriangles: number;
  waterUpdateCpuMs: number;
  rippleActiveCount: number;
  splashActiveCount: number;
  waterSplashDrawCalls: number;
  geologyDrawCalls: number;
  geologyTriangles: number;
  totalGeologyTriangles: number;
  visibleGeologyInstances: number;
  totalGeologyInstances: number;
  geologyBufferBytes: number;
  vegetationDrawCalls: number;
  vegetationTriangles: number;
  totalVegetationTriangles: number;
  visibleVegetationTrees: number;
  totalVegetationTrees: number;
  visibleVegetationShrubs: number;
  totalVegetationShrubs: number;
  vegetationBufferBytes: number;
  humanDrawCalls: number;
  humanTriangles: number;
  totalHumanTriangles: number;
  visibleHumanObjects: number;
  totalHumanObjects: number;
  humanBufferBytes: number;
  itemDrawCalls: number;
  itemTriangles: number;
  visibleItemEntities: number;
  totalItemEntities: number;
  itemBufferBytes: number;
  aiDrawCalls: number;
  aiTriangles: number;
  totalAiTriangles: number;
  visibleAiObjects: number;
  totalAiObjects: number;
  aiBufferBytes: number;
  characterDrawCalls: number;
  characterTriangles: number;
  visibleCharacters: number;
  totalCharacters: number;
  characterJoints: number;
  characterJointUpdates: number;
  characterAnimationCpuMs: number;
  characterBufferBytes: number;
  atmosphereDrawCalls: number;
  particleDrawCalls: number;
  particleCount: number;
  shadowDrawCalls: number;
  shadowTriangles: number;
  shadowMapSize: number;
  shadowTextureBytes: number;
  shadowCpuMs: number;
  selectedLocalLights: number;
  localShadowCpuMs: number;
  localShadowDrawCalls: number;
  localShadowTriangles: number;
  localShadowMapCount: number;
  localShadowMapSize: number;
  localShadowTextureBytes: number;
  localShadowCacheHits: number;
  /** CPU selection/packing only; fragment shader GPU cost is not measured here. */
  localLightSelectionCpuMs: number;
  contactShadowDrawCalls: number;
  contactShadowActors: number;
  contactShadowTriangles: number;
  textureBytes: number;
  instanceBufferBytes: number;
  instanceBufferGrowths: number;
  instanceBufferAllocatedBytesThisFrame: number;
  collisionCandidates: number;
  collisionCandidatesAverage: number;
  collisionCandidatesMax: number;
  surfaceDataBytes: number;
  width: number;
  height: number;
}

export class GpuMesh {
  readonly vertexArray: WebGLVertexArrayObject;
  readonly indexCount: number;
  readonly triangleCount: number;
  readonly bufferBytes: number;
  readonly surfaceDataBytes: number;
  private readonly positionBuffer: WebGLBuffer;
  private readonly normalBuffer: WebGLBuffer;
  private readonly surfaceBuffer?: WebGLBuffer;
  private readonly editorBiomeTintBuffer?: WebGLBuffer;
  private readonly editorBiomeTintBytes: number;
  private readonly positionBytes: number;
  private readonly normalBytes: number;
  private readonly instanceBuffer: WebGLBuffer;
  private readonly buffers: readonly WebGLBuffer[];
  private instanceCapacityBytes = 20 * Float32Array.BYTES_PER_ELEMENT;
  private disposed = false;

  get allocatedBufferBytes(): number {
    return this.bufferBytes + this.instanceCapacityBytes - 20 * Float32Array.BYTES_PER_ELEMENT;
  }

  get allocatedInstanceBytes(): number {
    return this.instanceCapacityBytes;
  }

  constructor(gl: WebGL2RenderingContext, geometry: MeshGeometry) {
    const vertexArray = gl.createVertexArray();
    const position = gl.createBuffer();
    const normal = gl.createBuffer();
    const index = gl.createBuffer();
    const instance = gl.createBuffer();
    if (!vertexArray || !position || !normal || !index || !instance) throw new Error("WebGL buffer allocation failed");
    this.vertexArray = vertexArray;
    this.indexCount = geometry.indices.length;
    this.triangleCount = geometry.indices.length / 3;
    this.surfaceDataBytes = geometry.surfaceWeights?.byteLength ?? 0;
    this.editorBiomeTintBytes = geometry.editorBiomeTint?.byteLength ?? 0;
    this.positionBuffer = position;
    this.normalBuffer = normal;
    this.positionBytes = geometry.positions.byteLength;
    this.normalBytes = geometry.normals.byteLength;
    this.instanceBuffer = instance;
    this.bufferBytes =
      geometry.positions.byteLength +
      geometry.normals.byteLength +
      geometry.indices.byteLength +
      (geometry.texcoords?.byteLength ?? 0) +
      this.surfaceDataBytes +
      this.editorBiomeTintBytes +
      this.instanceCapacityBytes;

    gl.bindVertexArray(vertexArray);
    attribute(gl, position, 0, geometry.positions);
    attribute(gl, normal, 1, geometry.normals);
    const buffers: WebGLBuffer[] = [position, normal, index, instance];
    if (geometry.surfaceWeights) {
      const surface = gl.createBuffer();
      if (!surface) throw new Error("WebGL surface buffer allocation failed");
      this.surfaceBuffer = surface;
      buffers.push(surface);
      gl.bindBuffer(gl.ARRAY_BUFFER, surface);
      gl.bufferData(gl.ARRAY_BUFFER, geometry.surfaceWeights, gl.STATIC_DRAW);
      gl.enableVertexAttribArray(2);
      gl.vertexAttribPointer(2, 4, gl.UNSIGNED_BYTE, true, 8, 0);
      gl.enableVertexAttribArray(3);
      gl.vertexAttribPointer(3, 4, gl.UNSIGNED_BYTE, true, 8, 4);
    }
    if (geometry.texcoords) {
      const texcoord = gl.createBuffer();
      if (!texcoord) throw new Error("WebGL texture coordinate buffer allocation failed");
      buffers.push(texcoord);
      gl.bindBuffer(gl.ARRAY_BUFFER, texcoord);
      gl.bufferData(gl.ARRAY_BUFFER, geometry.texcoords, gl.STATIC_DRAW);
      gl.enableVertexAttribArray(9);
      gl.vertexAttribPointer(9, 2, gl.FLOAT, false, 0, 0);
    }
    if (geometry.editorBiomeTint) {
      const tint = gl.createBuffer();
      if (!tint) throw new Error("WebGL editor tint buffer allocation failed");
      this.editorBiomeTintBuffer = tint;
      buffers.push(tint);
      gl.bindBuffer(gl.ARRAY_BUFFER, tint);
      gl.bufferData(gl.ARRAY_BUFFER, geometry.editorBiomeTint, gl.DYNAMIC_DRAW);
      gl.enableVertexAttribArray(10);
      gl.vertexAttribPointer(10, 4, gl.UNSIGNED_BYTE, true, 0, 0);
    }
    this.buffers = buffers;
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, index);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, geometry.indices, gl.STATIC_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, instance);
    gl.bufferData(gl.ARRAY_BUFFER, this.instanceCapacityBytes, gl.DYNAMIC_DRAW);
    const instanceStride = 20 * Float32Array.BYTES_PER_ELEMENT;
    for (let column = 0; column < 4; column += 1) {
      const location = 4 + column;
      gl.enableVertexAttribArray(location);
      gl.vertexAttribPointer(location, 4, gl.FLOAT, false, instanceStride, column * 4 * Float32Array.BYTES_PER_ELEMENT);
      gl.vertexAttribDivisor(location, 1);
    }
    gl.enableVertexAttribArray(8);
    gl.vertexAttribPointer(8, 4, gl.FLOAT, false, instanceStride, 16 * Float32Array.BYTES_PER_ELEMENT);
    gl.vertexAttribDivisor(8, 1);
    gl.bindVertexArray(null);
  }

  /** Deforms an existing mesh while retaining its indices, attributes and allocations. */
  updateGeometry(gl: WebGL2RenderingContext, geometry: Pick<MeshGeometry, "positions" | "normals">): void {
    if (this.disposed) return;
    if (geometry.positions.byteLength !== this.positionBytes || geometry.normals.byteLength !== this.normalBytes) {
      throw new RangeError("Mesh geometry updates must preserve vertex buffer sizes");
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, geometry.positions);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.normalBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, geometry.normals);
  }

  /** Changes editor paint without allocating or touching terrain geometry. */
  updateEditorPaint(gl: WebGL2RenderingContext, geometry: Pick<MeshGeometry, "editorBiomeTint" | "surfaceWeights">): void {
    if (this.disposed) return;
    if (!this.editorBiomeTintBuffer || !geometry.editorBiomeTint
      || geometry.editorBiomeTint.byteLength !== this.editorBiomeTintBytes) {
      throw new RangeError("Editor paint updates must preserve existing tint buffer sizes");
    }
    if (geometry.surfaceWeights && (!this.surfaceBuffer || geometry.surfaceWeights.byteLength !== this.surfaceDataBytes)) {
      throw new RangeError("Editor paint updates must preserve existing surface buffer sizes");
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, this.editorBiomeTintBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, geometry.editorBiomeTint);
    if (geometry.surfaceWeights && this.surfaceBuffer) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.surfaceBuffer);
      gl.bufferSubData(gl.ARRAY_BUFFER, 0, geometry.surfaceWeights);
    }
  }

  uploadInstances(gl: WebGL2RenderingContext, data: Float32Array, instanceCount: number): number {
    if (this.disposed) return 0;
    const byteLength = instanceCount * 20 * Float32Array.BYTES_PER_ELEMENT;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
    let allocatedBytes = 0;
    if (byteLength > this.instanceCapacityBytes) {
      const nextCapacity = Math.max(20 * Float32Array.BYTES_PER_ELEMENT, Math.ceil(byteLength / 256) * 256);
      gl.bufferData(gl.ARRAY_BUFFER, nextCapacity, gl.DYNAMIC_DRAW);
      allocatedBytes = nextCapacity - this.instanceCapacityBytes;
      this.instanceCapacityBytes = nextCapacity;
    }
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, data, 0, instanceCount * 20);
    return allocatedBytes;
  }

  dispose(gl: WebGL2RenderingContext): number {
    if (this.disposed) return 0;
    this.disposed = true;
    gl.deleteVertexArray(this.vertexArray);
    for (const buffer of this.buffers) gl.deleteBuffer(buffer);
    return this.allocatedBufferBytes;
  }
}

export interface StaticRenderMaterial {
  readonly texture?: WebGLTexture;
  readonly alphaCutoff: number;
  readonly doubleSided: boolean;
  readonly emissiveColor?: readonly [number, number, number];
  readonly emissiveStrength?: number;
  readonly pulseStrength?: number;
}

export type StaticRenderKind = "geology" | "vegetation" | "human" | "ai" | "item";

const NO_EMISSIVE = [0, 0, 0] as const;

function attribute(gl: WebGL2RenderingContext, buffer: WebGLBuffer, location: number, data: Float32Array): void {
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(location);
  gl.vertexAttribPointer(location, 3, gl.FLOAT, false, 0, 0);
}

const vertexSource = `#version 300 es
precision highp float;
layout(location=0) in vec3 aPosition;
layout(location=1) in vec3 aNormal;
layout(location=2) in vec4 aSurfaceA;
layout(location=3) in vec4 aSurfaceB;
layout(location=4) in vec4 aInstance0;
layout(location=5) in vec4 aInstance1;
layout(location=6) in vec4 aInstance2;
layout(location=7) in vec4 aInstance3;
layout(location=8) in vec4 aInstanceTint;
layout(location=9) in vec2 aTexcoord;
layout(location=10) in vec4 aEditorBiomeTint;
uniform mat4 uViewProjection;
uniform mat4 uModel;
uniform vec4 uTint;
uniform float uInstanced;
out vec3 vNormal;
out vec3 vWorld;
out vec4 vSurfaceA;
out vec4 vSurfaceB;
out vec4 vTint;
out vec2 vTexcoord;
out vec4 vEditorBiomeTint;
void main() {
  mat4 model = uInstanced > 0.5 ? mat4(aInstance0, aInstance1, aInstance2, aInstance3) : uModel;
  vec4 world = model * vec4(aPosition, 1.0);
  vWorld = world.xyz;
  vNormal = normalize(mat3(model) * aNormal);
  vSurfaceA = aSurfaceA;
  vSurfaceB = aSurfaceB;
  vTint = uInstanced > 0.5 ? aInstanceTint : uTint;
  vTexcoord = aTexcoord;
  vEditorBiomeTint = aEditorBiomeTint;
  gl_Position = uViewProjection * world;
}`;

function fragmentSource(advancedLighting: boolean): string {
  return `#version 300 es
precision highp float;
in vec3 vNormal;
in vec3 vWorld;
in vec4 vSurfaceA;
in vec4 vSurfaceB;
in vec4 vTint;
in vec2 vTexcoord;
in vec4 vEditorBiomeTint;
uniform vec3 uCamera;
uniform float uMaterial;
uniform sampler2D uBaseColorTexture;
uniform float uTextured;
uniform float uAlphaCutoff;
uniform vec3 uViewForward;
uniform vec3 uSunDirection;
uniform vec3 uAmbientColor;
uniform vec3 uSunColor;
uniform vec3 uFogColor;
uniform float uFogNear;
uniform float uFogFar;
uniform float uFogMaximum;
uniform float uTpsAtmosphere;
uniform float uLightingEnabled;
uniform float uFogEnabled;
uniform float uGradingEnabled;
uniform vec3 uEmissiveColor;
uniform float uEmissiveStrength;
uniform float uPulseStrength;
uniform float uAiGlowEnabled;
uniform float uTime;
uniform float uSnowCover;
uniform float uGroundWetness;
out vec4 outColor;

${TPS_SKY_COLOR_GLSL}
${advancedLighting ? LIGHTING_GLSL : ""}
${SNOW_SURFACE_GLSL}

void main() {
  vec3 smoothNormal = normalize(vNormal);
  vec3 faceNormal = normalize(cross(dFdx(vWorld), dFdy(vWorld)));
  if (faceNormal.y < 0.0) faceNormal = -faceNormal;
  vec3 color;
  vec3 shadeNormal = faceNormal;
  float sunlight = 0.0;
  vec4 baseSample = vec4(1.0);
  if (uTextured > 0.5) baseSample = texture(uBaseColorTexture, vTexcoord);
  if (baseSample.a * vTint.a < uAlphaCutoff) discard;

  if (uMaterial < 0.5) {
    // Studio uses smooth authored blends; gameplay retains its existing bands.
    vec4 weightsA = uMaterial < -0.5 ? vSurfaceA : floor(vSurfaceA * 6.0 + 0.5) / 6.0;
    vec4 weightsB = uMaterial < -0.5 ? vSurfaceB : floor(vSurfaceB * 6.0 + 0.5) / 6.0;
    float weightTotal = max(0.001, dot(weightsA, vec4(1.0)) + weightsB.x + weightsB.y + weightsB.z);
    color = vec3(0.0);
    color += vec3(0.420, 0.340, 0.240) * weightsA.x;
    color += vec3(0.330, 0.335, 0.325) * weightsA.y;
    color += vec3(0.255, 0.235, 0.205) * weightsA.z;
    color += vec3(0.440, 0.360, 0.205) * weightsA.w;
    color += vec3(0.220, 0.440, 0.150) * weightsB.x;
    color += vec3(0.210, 0.200, 0.190) * weightsB.y;
    color += vec3(0.190, 0.235, 0.235) * weightsB.z;
    color /= weightTotal;
    float broadTone = sin(vWorld.x * 0.041 + vWorld.z * 0.027) * 0.5 +
                      sin(vWorld.z * 0.023 - vWorld.x * 0.017) * 0.5;
    float toneBand = floor((broadTone * 0.5 + 0.5) * 4.0 + 0.5) / 4.0;
    float slope = 1.0 - clamp(smoothNormal.y, 0.0, 1.0);
    color *= mix(0.92, 1.055, toneBand);
    color = mix(color, vec3(0.225, 0.224, 0.211), min(0.34, slope * 1.05));
    shadeNormal = normalize(mix(smoothNormal, faceNormal, 0.68));
    // Negative material values are exclusive to the Studio terrain draw path.
    // Premultiplied coverage keeps empty/feathered vertices free of dark edges.
    if (uMaterial < -1.5) color = color * (1.0 - vEditorBiomeTint.a) + vEditorBiomeTint.rgb;
  } else if (uMaterial < 1.5) {
    color = vTint.rgb;
    shadeNormal = smoothNormal;
  } else if (uMaterial < 2.5) {
    color = vec3(0.120, 0.270, 0.300);
    float waterBand = floor((sin(vWorld.x * 0.08 + vWorld.z * 0.045) * 0.5 + 0.5) * 3.0 + 0.5) / 3.0;
    color *= mix(0.91, 1.045, waterBand);
    shadeNormal = smoothNormal;
  } else {
    color = vTint.rgb * baseSample.rgb;
    // Authored static normals preserve wall orientation. Flipping derivative
    // normals by a near-zero Y component produces speckled vertical lighting.
    shadeNormal = smoothNormal;
    float brokenFace = step(0.72, faceNormal.y) * 0.055;
    color += vec3(brokenFace, brokenFace * 0.84, brokenFace * 0.62);
  }

  bool snowyTerrain = uMaterial >= 0.0 && uMaterial < 0.5;
  bool snowyStatic = uMaterial >= 2.5 && uMaterial < 3.5 && uAlphaCutoff < 0.01;
  if (uDynamicEnvironment > 0.5 && (snowyTerrain || snowyStatic)) {
    float snow = snowSurfaceMask(vWorld, smoothNormal, uSnowCover);
    if (snowyTerrain) color *= 1.0 - uGroundWetness * 0.21;
    color = mix(color, snowSurfaceColor(vWorld, color), snow * 0.96);
  }
  sunlight = max(0.0, dot(shadeNormal, normalize(uSunDirection)));
  float lightBand = floor(sunlight * 5.0 + 0.5) / 5.0;
  ${advancedLighting ? `vec3 lightColor = vec3(0.82);
  if (uLightingEnabled > 0.5) {
    float surfaceLightBand = uMaterial > 1.5 && uMaterial < 2.5 ? 0.25 : lightBand;
    lightColor = worldLighting(vWorld, shadeNormal, surfaceLightBand);
  }` : `vec3 lightColor = uAmbientColor + uSunColor * lightBand;
  if (uMaterial > 1.5 && uMaterial < 2.5) lightColor = uAmbientColor + uSunColor * 0.25;
  if (uLightingEnabled < 0.5) lightColor = vec3(0.82);`}
  float pulse = 1.0;
  if (uAiGlowEnabled > 0.5 && uPulseStrength > 0.0) {
    pulse -= uPulseStrength * (0.5 + sin(uTime * 1.04719755) * 0.5);
  }
  vec3 emissive = uEmissiveColor * uEmissiveStrength * pulse * uAiGlowEnabled;
  vec3 shaded = color * lightColor + emissive;

  if (uGradingEnabled > 0.5) {
    float luma = dot(shaded, vec3(0.299, 0.587, 0.114));
    shaded = mix(vec3(luma), shaded, 0.64);
    float coolShadow = (1.0 - lightBand) * 0.42;
    shaded *= mix(vec3(0.98, 0.98, 0.97), vec3(0.90, 0.97, 1.04), coolShadow);
    shaded = clamp((shaded - vec3(0.24)) * 1.055 + vec3(0.24), 0.0, 1.0);
    shaded = floor(shaded * 48.0 + 0.5) / 48.0;
  }

  vec3 cameraRay = vWorld - uCamera;
  float viewDepth = uTpsAtmosphere > 0.5 ? length(cameraRay) : dot(cameraRay, normalize(uViewForward));
  float distanceFog = smoothstep(uFogNear, uFogFar, viewDepth) * uFogMaximum;
  float lowland = 1.0 - smoothstep(-1.2, 2.8, vWorld.y);
  float heightHaze = lowland * smoothstep(uFogNear + 5.0, uFogFar - 5.0, viewDepth) * 0.10;
  float fog = uFogEnabled > 0.5 ? min(uFogMaximum, distanceFog + heightHaze) : 0.0;
  if (fog > 0.001) {
    vec3 fogColor = uTpsAtmosphere > 0.5 ? tpsSkyColor(normalize(cameraRay)) : uFogColor;
    shaded = mix(shaded, fogColor, fog);
  }
  outColor = vec4(shaded, vTint.a);
}`;
}

const shadowFragmentSource = `#version 300 es
precision highp float;
in vec4 vTint;
in vec2 vTexcoord;
uniform sampler2D uBaseColorTexture;
uniform float uTextured;
uniform float uAlphaCutoff;
void main() {
  float alpha = vTint.a;
  if (uTextured > 0.5) alpha *= texture(uBaseColorTexture, vTexcoord).a;
  if (alpha < uAlphaCutoff) discard;
}`;

const skyVertexSource = `#version 300 es
precision highp float;
uniform mat4 uViewProjection;
out vec3 vDirection;
void main() {
  vec2 position = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2)) * 2.0 - 1.0;
  // Recover perspective rays from the rotation/projection rows. Omitting the
  // translation avoids subtracting large world positions near the camera.
  vec3 right = vec3(uViewProjection[0][0], uViewProjection[1][0], uViewProjection[2][0]);
  vec3 up = vec3(uViewProjection[0][1], uViewProjection[1][1], uViewProjection[2][1]);
  vec3 forward = vec3(uViewProjection[0][3], uViewProjection[1][3], uViewProjection[2][3]);
  vDirection = normalize(forward) + position.x * right / dot(right, right) + position.y * up / dot(up, up);
  gl_Position = vec4(position, 0.0, 1.0);
}`;

const skyFragmentSource = `#version 300 es
precision highp float;
in vec3 vDirection;
uniform vec3 uFogColor;
out vec4 outColor;
${TPS_SKY_COLOR_GLSL}
void main() {
  outColor = vec4(tpsSkyColor(normalize(vDirection)), 1.0);
}`;

const particleVertexSource = `#version 300 es
precision highp float;
layout(location=0) in vec4 aParticle;
uniform mat4 uViewProjection;
uniform vec3 uCenter;
uniform vec3 uCamera;
uniform vec3 uViewForward;
uniform float uTime;
uniform float uPointScale;
uniform float uFogNear;
uniform float uFogFar;
uniform float uFogMaximum;
uniform float uFogEnabled;
uniform float uTpsAtmosphere;
uniform vec2 uAmbientWind;
uniform float uDynamicParticles;
out float vAlpha;
out float vSeed;

float hash(float value) {
  return fract(sin(value * 91.733) * 43758.5453);
}

void main() {
  float seed = aParticle.w;
  float speed = mix(0.006, 0.013, hash(seed + 0.17));
  float travel = fract(seed + uTime * speed);
  // Weather changes this bounded per-life displacement, never an absolute
  // clock multiplier. Studio retains the existing ash movement.
  vec2 wind = mix(vec2(16.0, -7.0), uAmbientWind, uDynamicParticles) * (travel - 0.5);
  vec3 world = uCenter + vec3(
    aParticle.x + wind.x,
    aParticle.y + sin(uTime * 0.24 + seed * 31.0) * 0.42,
    aParticle.z + wind.y + sin(uTime * 0.11 + seed * 17.0) * 1.1
  );
  float viewDepth = uTpsAtmosphere > 0.5 ? length(world - uCamera) : dot(world - uCamera, normalize(uViewForward));
  float fog = uFogEnabled > 0.5 ? smoothstep(uFogNear, uFogFar, viewDepth) * uFogMaximum : 0.0;
  float lifeFade = smoothstep(0.0, 0.09, travel) * (1.0 - smoothstep(0.90, 1.0, travel));
  vAlpha = mix(0.18, 0.34, hash(seed + 0.53)) * lifeFade * (1.0 - fog * 0.72);
  vSeed = seed;
  gl_PointSize = mix(1.15, 2.35, hash(seed + 0.81)) * uPointScale;
  gl_Position = uViewProjection * vec4(world, 1.0);
}`;

const particleFragmentSource = `#version 300 es
precision highp float;
in float vAlpha;
in float vSeed;
out vec4 outColor;

void main() {
  vec3 ash = mix(vec3(0.46, 0.45, 0.41), vec3(0.36, 0.43, 0.45), fract(vSeed * 7.31));
  outColor = vec4(ash, vAlpha);
}`;

export class Renderer {
  readonly gl: WebGL2RenderingContext;
  readonly metrics: RendererMetrics = {
    fps: 0,
    frameMs: 0,
    cpuMs: 0,
    drawCalls: 0,
    triangles: 0,
    totalTerrainTriangles: 0,
    visibleTerrainChunks: 0,
    totalTerrainChunks: 0,
    waterDrawCalls: 0,
    waterTriangles: 0,
    waterUpdateCpuMs: 0,
    rippleActiveCount: 0,
    splashActiveCount: 0,
    waterSplashDrawCalls: 0,
    geologyDrawCalls: 0,
    geologyTriangles: 0,
    totalGeologyTriangles: 0,
    visibleGeologyInstances: 0,
    totalGeologyInstances: 0,
    geologyBufferBytes: 0,
    vegetationDrawCalls: 0,
    vegetationTriangles: 0,
    totalVegetationTriangles: 0,
    visibleVegetationTrees: 0,
    totalVegetationTrees: 0,
    visibleVegetationShrubs: 0,
    totalVegetationShrubs: 0,
    vegetationBufferBytes: 0,
    humanDrawCalls: 0,
    humanTriangles: 0,
    totalHumanTriangles: 0,
    visibleHumanObjects: 0,
    totalHumanObjects: 0,
    humanBufferBytes: 0,
    itemDrawCalls: 0,
    itemTriangles: 0,
    visibleItemEntities: 0,
    totalItemEntities: 0,
    itemBufferBytes: 0,
    aiDrawCalls: 0,
    aiTriangles: 0,
    totalAiTriangles: 0,
    visibleAiObjects: 0,
    totalAiObjects: 0,
    aiBufferBytes: 0,
    characterDrawCalls: 0,
    characterTriangles: 0,
    visibleCharacters: 0,
    totalCharacters: 0,
    characterJoints: 0,
    characterJointUpdates: 0,
    characterAnimationCpuMs: 0,
    characterBufferBytes: 0,
    atmosphereDrawCalls: 0,
    particleDrawCalls: 0,
    particleCount: 0,
    shadowDrawCalls: 0,
    shadowTriangles: 0,
    shadowMapSize: 0,
    shadowTextureBytes: 0,
    shadowCpuMs: 0,
    selectedLocalLights: 0,
    localShadowCpuMs: 0, localShadowDrawCalls: 0, localShadowTriangles: 0,
    localShadowMapCount: 0, localShadowMapSize: 0, localShadowTextureBytes: 0, localShadowCacheHits: 0,
    localLightSelectionCpuMs: 0,
    contactShadowDrawCalls: 0,
    contactShadowActors: 0,
    contactShadowTriangles: 0,
    textureBytes: 0,
    instanceBufferBytes: 0,
    instanceBufferGrowths: 0,
    instanceBufferAllocatedBytesThisFrame: 0,
    collisionCandidates: 0,
    collisionCandidatesAverage: 0,
    collisionCandidatesMax: 0,
    surfaceDataBytes: 0,
    width: 0,
    height: 0,
  };
  meshMemoryBytes = 0;
  private readonly program: WebGLProgram;
  private readonly lightingUniforms: LightingUniforms | null;
  private readonly localLightSelection = new LocalLightSelection();
  private localLights: readonly LocalLight[] = [];
  private lightingQuality: AtmosphereQuality = "medium";
  private readonly shadowCamera = new DirectionalShadowCamera();
  private currentShadowCamera: Camera = this.shadowCamera;
  private localShadowActive = false;
  private localPassTriangles = 0;
  private staticShadowRevision = 0;
  private localShadowPreviousTime = 0;
  private readonly localShadowSelection = new LocalShadowSelection();
  private readonly localShadowCamera = new LocalShadowCamera();
  private readonly localShadowResources = new Map<string, LocalShadowResource>();
  private readonly localShadowMatrices = new Float32Array(12 * 16);
  private readonly localShadowInfo = new Float32Array(8);
  private readonly localShadowTextures: (WebGLTexture | undefined)[] = [];
  private readonly failedLocalShadows = new Set<string>();
  private environment: EnvironmentPresentation | null = null;
  private legacyEnvironmentFog: AtmosphereFogProfile | null = null;
  private readonly cloudOffset: [number, number] = [0, 0];
  private readonly environmentUniforms = new Map<WebGLProgram, Record<string, WebGLUniformLocation | null>>();
  private shadowActive = false;
  private shadowReady = false;
  private shadowStartedAt = 0;
  private shadowResources: {
    readonly size: number;
    readonly texture: WebGLTexture;
    readonly framebuffer: WebGLFramebuffer;
    readonly program: WebGLProgram;
    readonly uniforms: {
      viewProjection: WebGLUniformLocation | null; model: WebGLUniformLocation | null;
      tint: WebGLUniformLocation | null; instanced: WebGLUniformLocation | null;
      textured: WebGLUniformLocation | null; alphaCutoff: WebGLUniformLocation | null;
      baseColorTexture: WebGLUniformLocation | null;
    };
  } | undefined;
  private readonly particleProgram: WebGLProgram;
  private readonly particleVertexArray: WebGLVertexArrayObject;
  private sky: {
    readonly program: WebGLProgram;
    readonly viewProjection: WebGLUniformLocation;
    readonly fogColor: WebGLUniformLocation;
  } | undefined;
  private readonly whiteTexture: WebGLTexture;
  private readonly uniforms: {
    viewProjection: WebGLUniformLocation;
    model: WebGLUniformLocation;
    tint: WebGLUniformLocation;
    camera: WebGLUniformLocation;
    material: WebGLUniformLocation;
    instanced: WebGLUniformLocation;
    textured: WebGLUniformLocation;
    alphaCutoff: WebGLUniformLocation;
    baseColorTexture: WebGLUniformLocation;
    viewForward: WebGLUniformLocation;
    sunDirection: WebGLUniformLocation;
    ambientColor: WebGLUniformLocation;
    sunColor: WebGLUniformLocation;
    fogColor: WebGLUniformLocation;
    fogNear: WebGLUniformLocation;
    fogFar: WebGLUniformLocation;
    fogMaximum: WebGLUniformLocation;
    tpsAtmosphere: WebGLUniformLocation;
    lightingEnabled: WebGLUniformLocation;
    fogEnabled: WebGLUniformLocation;
    gradingEnabled: WebGLUniformLocation;
    emissiveColor: WebGLUniformLocation;
    emissiveStrength: WebGLUniformLocation;
    pulseStrength: WebGLUniformLocation;
    aiGlowEnabled: WebGLUniformLocation;
    time: WebGLUniformLocation;
  };
  private readonly particleUniforms: {
    viewProjection: WebGLUniformLocation;
    center: WebGLUniformLocation;
    camera: WebGLUniformLocation;
    viewForward: WebGLUniformLocation;
    time: WebGLUniformLocation;
    pointScale: WebGLUniformLocation;
    fogNear: WebGLUniformLocation;
    fogFar: WebGLUniformLocation;
    fogMaximum: WebGLUniformLocation;
    fogEnabled: WebGLUniformLocation;
    tpsAtmosphere: WebGLUniformLocation;
    ambientWind: WebGLUniformLocation;
    dynamicParticles: WebGLUniformLocation;
  };
  private readonly identity = mat4Identity();
  private resolutionScale = 1;
  private maximumWidth = 1_040;
  private cssWidth = 1;
  private cssHeight = 1;
  private frameStartedAt = 0;
  private previousFrameAt: number | null = null;
  private smoothedFrameMs = 16.7;
  private smoothedCpuMs = 1;
  private readonly texturePromises = new Map<string, Promise<WebGLTexture>>();
  private readonly textureAllocationBytes = new Map<WebGLTexture, number>();
  private readonly textureRetainers = new Map<string, number>();
  private atmosphereFlags: AtmosphereFeatureFlags = DEFAULT_ATMOSPHERE_FLAGS;
  private particleCount = particleCountForQuality("medium");
  private biomeAtmosphere: BiomeAtmosphereProfile = BIOME_ATMOSPHERE_PROFILES.ASHLANDS;
  private tpsAtmosphere = false;
  private tpsFog: AtmosphereFogProfile = tpsFogProfile(this.biomeAtmosphere);
  private clearColor: readonly [number, number, number] | null = null;
  private readonly viewForward: [number, number, number] = [0, -1, 0];
  private aiGlowEnabled = true;
  private underwaterAmount = 0;
  private readonly underwaterFog = { fogColor: [0, 0, 0] as [number, number, number], fogNear: 0, fogFar: 0, fogMaximum: 1 };
  private readonly waterEffects = new WaterInteractionEffects();
  private waterUpdateCpuMs = 0;
  private waterFrameBound = false;
  private frameTimeSeconds = 0;
  private frameViewProjection: Mat4 = mat4Identity();
  private readonly frameCamera: [number, number, number] = [0, 0, 0];
  private waterResources: {
    readonly program: WebGLProgram;
    readonly lighting: LightingUniforms;
    readonly uniforms: Record<string, WebGLUniformLocation | null>;
    readonly splashProgram: WebGLProgram;
    readonly splashUniforms: Record<string, WebGLUniformLocation | null>;
  } | undefined;

  constructor(
    readonly canvas: HTMLCanvasElement,
    private readonly options: {
      readonly responsiveResolution?: boolean;
      /** Gameplay opts in; Studio keeps its original shader and preview appearance. */
      readonly advancedLighting?: boolean;
    } = {},
  ) {
    const gl = canvas.getContext("webgl2", {
      alpha: false,
      antialias: false,
      depth: true,
      powerPreference: "high-performance",
      preserveDrawingBuffer: false,
    });
    if (!gl) throw new Error("WebGL 2 is not available");
    this.gl = gl;
    const whiteTexture = gl.createTexture();
    if (!whiteTexture) throw new Error("WebGL fallback texture allocation failed");
    this.whiteTexture = whiteTexture;
    gl.bindTexture(gl.TEXTURE_2D, whiteTexture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([255, 255, 255, 255]));
    this.program = createProgram(gl, vertexSource, fragmentSource(options.advancedLighting === true));
    this.lightingUniforms = options.advancedLighting ? createLightingUniforms(gl, this.program) : null;
    const uniform = (name: string): WebGLUniformLocation => {
      const location = gl.getUniformLocation(this.program, name);
      if (location === null) throw new Error(`Missing shader uniform ${name}`);
      return location;
    };
    this.uniforms = {
      viewProjection: uniform("uViewProjection"),
      model: uniform("uModel"),
      tint: uniform("uTint"),
      camera: uniform("uCamera"),
      material: uniform("uMaterial"),
      instanced: uniform("uInstanced"),
      textured: uniform("uTextured"),
      alphaCutoff: uniform("uAlphaCutoff"),
      baseColorTexture: uniform("uBaseColorTexture"),
      viewForward: uniform("uViewForward"),
      sunDirection: uniform("uSunDirection"),
      ambientColor: uniform("uAmbientColor"),
      sunColor: uniform("uSunColor"),
      fogColor: uniform("uFogColor"),
      fogNear: uniform("uFogNear"),
      fogFar: uniform("uFogFar"),
      fogMaximum: uniform("uFogMaximum"),
      tpsAtmosphere: uniform("uTpsAtmosphere"),
      lightingEnabled: uniform("uLightingEnabled"),
      fogEnabled: uniform("uFogEnabled"),
      gradingEnabled: uniform("uGradingEnabled"),
      emissiveColor: uniform("uEmissiveColor"),
      emissiveStrength: uniform("uEmissiveStrength"),
      pulseStrength: uniform("uPulseStrength"),
      aiGlowEnabled: uniform("uAiGlowEnabled"),
      time: uniform("uTime"),
    };
    this.particleProgram = createProgram(gl, particleVertexSource, particleFragmentSource);
    const particleUniform = (name: string): WebGLUniformLocation => {
      const location = gl.getUniformLocation(this.particleProgram, name);
      if (location === null) throw new Error(`Missing particle shader uniform ${name}`);
      return location;
    };
    this.particleUniforms = {
      viewProjection: particleUniform("uViewProjection"),
      center: particleUniform("uCenter"),
      camera: particleUniform("uCamera"),
      viewForward: particleUniform("uViewForward"),
      time: particleUniform("uTime"),
      pointScale: particleUniform("uPointScale"),
      fogNear: particleUniform("uFogNear"),
      fogFar: particleUniform("uFogFar"),
      fogMaximum: particleUniform("uFogMaximum"),
      fogEnabled: particleUniform("uFogEnabled"),
      tpsAtmosphere: particleUniform("uTpsAtmosphere"),
      ambientWind: particleUniform("uAmbientWind"),
      dynamicParticles: particleUniform("uDynamicParticles"),
    };
    const particleVertexArray = gl.createVertexArray();
    const particleBuffer = gl.createBuffer();
    if (!particleVertexArray || !particleBuffer) throw new Error("Atmosphere particle buffer allocation failed");
    this.particleVertexArray = particleVertexArray;
    gl.bindVertexArray(particleVertexArray);
    gl.bindBuffer(gl.ARRAY_BUFFER, particleBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, createAtmosphereParticleData(), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 4, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
    gl.enable(gl.DEPTH_TEST);
    gl.enable(gl.CULL_FACE);
    gl.cullFace(gl.BACK);
    this.resize();
  }

  createMesh(geometry: MeshGeometry): GpuMesh {
    const mesh = new GpuMesh(this.gl, geometry);
    this.meshMemoryBytes += mesh.bufferBytes;
    this.staticShadowRevision += 1;
    this.metrics.instanceBufferBytes += 20 * Float32Array.BYTES_PER_ELEMENT;
    return mesh;
  }

  updateMeshGeometry(mesh: GpuMesh, geometry: Pick<MeshGeometry, "positions" | "normals">): void {
    mesh.updateGeometry(this.gl, geometry);
    this.staticShadowRevision += 1;
  }

  updateMeshEditorPaint(mesh: GpuMesh, geometry: Pick<MeshGeometry, "editorBiomeTint" | "surfaceWeights">): void {
    mesh.updateEditorPaint(this.gl, geometry);
  }

  destroyMesh(mesh: GpuMesh): void {
    const instanceBytes = mesh.allocatedInstanceBytes;
    const released = mesh.dispose(this.gl);
    if (released === 0) return;
    this.staticShadowRevision += 1;
    this.meshMemoryBytes = Math.max(0, this.meshMemoryBytes - released);
    this.metrics.instanceBufferBytes = Math.max(0, this.metrics.instanceBufferBytes - instanceBytes);
    this.metrics.surfaceDataBytes = Math.max(0, this.metrics.surfaceDataBytes - mesh.surfaceDataBytes);
  }

  loadTexture(url: string): Promise<WebGLTexture> {
    const existing = this.texturePromises.get(url);
    if (existing) return existing;
    const promise = this.createTexture(url);
    this.texturePromises.set(url, promise);
    return promise;
  }

  /** Context-wide ownership allows retiring/replacing Studio caches during in-flight loads. */
  acquireTexture(url: string): Promise<WebGLTexture> {
    this.textureRetainers.set(url, (this.textureRetainers.get(url) ?? 0) + 1);
    return this.loadTexture(url);
  }

  /** Explicit ownership release used by the private Studio asset cache. */
  releaseTexture(url: string): void {
    const retainers = this.textureRetainers.get(url) ?? 0;
    if (retainers > 1) { this.textureRetainers.set(url, retainers - 1); return; }
    this.textureRetainers.delete(url);
    const pending = this.texturePromises.get(url);
    if (!pending) return;
    this.texturePromises.delete(url);
    void pending.then((texture) => {
      this.gl.deleteTexture(texture);
      this.metrics.textureBytes = Math.max(0, this.metrics.textureBytes - (this.textureAllocationBytes.get(texture) ?? 0));
      this.textureAllocationBytes.delete(texture);
    }, () => undefined);
  }

  setQuality(resolutionScale: number, maximumWidth: number, atmosphereQuality: AtmosphereQuality): void {
    this.resolutionScale = resolutionScale;
    this.maximumWidth = maximumWidth;
    this.particleCount = particleCountForQuality(atmosphereQuality);
    this.lightingQuality = atmosphereQuality;
    this.applyResolution();
  }

  setAtmosphereFlags(flags: AtmosphereFeatureFlags): void {
    this.atmosphereFlags = flags;
  }

  get atmosphereFeatureFlags(): AtmosphereFeatureFlags { return this.atmosphereFlags; }

  setLocalLights(lights: readonly LocalLight[]): void {
    if (this.options.advancedLighting) this.localLights = lights;
  }

  get shadowPassActive(): boolean { return this.shadowActive; }
  get shadowViewProjection(): Mat4 { return this.currentShadowCamera.viewProjection; }

  setEnvironmentPresentation(presentation: EnvironmentPresentation): void {
    if (!this.options.advancedLighting) return;
    const dt = this.environment ? Math.max(0, Math.min(1, presentation.timeSeconds - this.environment.timeSeconds)) : 0;
    // Integrate wind velocity. Multiplying an absolute clock by changing wind
    // speed would teleport the cloud field during a weather transition.
    this.cloudOffset[0] = ((this.cloudOffset[0] + Math.cos(presentation.windDirectionRadians) * presentation.windSpeed * dt * 0.0008) % 4096 + 4096) % 4096;
    this.cloudOffset[1] = ((this.cloudOffset[1] + Math.sin(presentation.windDirectionRadians) * presentation.windSpeed * dt * 0.0008) % 4096 + 4096) % 4096;
    this.environment = composeBiomeEnvironment(presentation, this.biomeAtmosphere);
    this.legacyEnvironmentFog = legacyEnvironmentFogProfile(this.environment, this.biomeAtmosphere);
  }

  bindEnvironmentUniforms(program: WebGLProgram): void {
    const gl = this.gl;
    let uniforms = this.environmentUniforms.get(program);
    if (!uniforms) {
      uniforms = Object.fromEntries([...ENVIRONMENT_SKY_UNIFORM_NAMES, "uSnowCover", "uGroundWetness"].map((name) => [name, gl.getUniformLocation(program, name)]));
      this.environmentUniforms.set(program, uniforms);
    }
    const env = this.environment;
    gl.uniform1f(uniforms.uDynamicEnvironment!, env ? 1 : 0);
    if (!env) return;
    gl.uniform3f(uniforms.uSkyZenithColor!, ...env.skyZenithColor);
    gl.uniform3f(uniforms.uSkySunDirection!, ...env.sunDirection);
    gl.uniform3f(uniforms.uSkySunColor!, ...env.sunColor);
    gl.uniform1f(uniforms.uCloudCoverage!, env.cloudCoverage);
    gl.uniform2f(uniforms.uCloudOffset!, ...this.cloudOffset);
    gl.uniform1f(uniforms.uSnowCover!, env.snowCover);
    gl.uniform1f(uniforms.uGroundWetness!, env.groundWetness);
  }

  invalidateLocalShadowCache(): void { this.staticShadowRevision += 1; }

  isLocalShadowCaster(x: number, y: number, z: number, radius: number): boolean {
    return this.options.advancedLighting === true && this.atmosphereFlags.lighting
      && this.localShadowSelection.selected.some((light) => Math.hypot(x - light.position[0], y - light.position[1], z - light.position[2]) <= light.radius + radius);
  }

  canDrawShadow(triangles: number): boolean { return triangles <= this.remainingShadowTriangles; }

  private get remainingShadowTriangles(): number {
    return this.localShadowActive
      ? LIGHTING_CONFIG.quality[this.lightingQuality].localShadowTriangles / 6 - this.localPassTriangles
      : LIGHTING_CONFIG.quality[this.lightingQuality].maximumShadowTriangles - this.metrics.shadowTriangles;
  }

  /** Call once after the directional pass and before begin(). Each point/spot
   * source has six perspective faces; cached static + current actor depth are
   * independently compared in that source's own contribution in the shader. */
  renderLocalShadows(
    focus: readonly [number, number, number], timeSeconds: number,
    drawStatic: (camera: Camera) => void, drawDynamic: (camera: Camera) => void,
  ): void {
    this.metrics.localShadowDrawCalls = 0; this.metrics.localShadowTriangles = 0;
    this.metrics.localShadowCpuMs = 0; this.metrics.localShadowCacheHits = 0;
    this.localShadowInfo.fill(0); this.localShadowTextures.length = 0;
    if (!this.options.advancedLighting || !this.atmosphereFlags.lighting) return;
    const started = performance.now();
    const gl = this.gl;
    const budget = LIGHTING_CONFIG.quality[this.lightingQuality];
    const delta = Math.min(0.1, Math.max(0, timeSeconds - this.localShadowPreviousTime));
    this.localShadowPreviousTime = timeSeconds;
    this.localLightSelection.update(this.localLights, focus, this.lightingQuality, timeSeconds);
    this.localShadowSelection.update(this.localLightSelection.selected, focus, this.lightingQuality);
    for (const [id, resource] of this.localShadowResources) {
      const light = this.localShadowSelection.selected.find((entry) => entry.id === id);
      if (!light || resource.size !== budget.localShadowSize) {
        destroyLocalShadow(gl, resource); this.localShadowResources.delete(id);
      }
    }
    // Reuse the alpha-tested static depth program even when the sun is down.
    this.ensureShadowResources();
    gl.enable(gl.DEPTH_TEST); gl.depthMask(true); gl.disable(gl.BLEND);
    gl.enable(gl.CULL_FACE); gl.cullFace(gl.BACK);
    gl.enable(gl.POLYGON_OFFSET_FILL); gl.polygonOffset(0.7, 1);
    this.shadowActive = true; this.localShadowActive = true;
    this.currentShadowCamera = this.localShadowCamera;
    try {
      for (let slot = 0; slot < this.localShadowSelection.selected.length; slot += 1) {
        const light = this.localShadowSelection.selected[slot]!;
        let resource = this.localShadowResources.get(light.id);
        if (!resource && !this.failedLocalShadows.has(light.id)) {
          gl.activeTexture(gl.TEXTURE0);
          resource = createLocalShadow(gl, light, budget.localShadowSize);
          if (resource) this.localShadowResources.set(light.id, resource);
          else {
            // Bound failure diagnostics/storage; all failed resources fall back
            // to an ordinary unshadowed source without breaking the session.
            if (this.failedLocalShadows.size < 32) this.failedLocalShadows.add(light.id);
          }
        }
        if (!resource) continue;
        const signature = localShadowSignature(light);
        const refreshStatic = resource.revision !== this.staticShadowRevision || resource.signature !== signature;
        if (!refreshStatic) this.metrics.localShadowCacheHits += 1;
        for (let layer = 0; layer < 2; layer += 1) {
          if (layer === 0 && !refreshStatic) continue;
          gl.bindFramebuffer(gl.FRAMEBUFFER, resource.framebuffers[layer]!);
          gl.clear(gl.DEPTH_BUFFER_BIT);
          for (let face = 0; face < 6; face += 1) {
            // Reserve a separate share for every direction: earlier faces can
            // never consume the downward face's terrain/caster budget.
            this.localPassTriangles = 0;
            this.localShadowCamera.updateLight(light, face);
            this.localShadowMatrices.set(this.localShadowCamera.viewProjection, (slot * 6 + face) * 16);
            gl.viewport((face % 3) * resource.size, Math.floor(face / 3) * resource.size, resource.size, resource.size);
            if (layer === 0) drawStatic(this.localShadowCamera);
            else drawDynamic(this.localShadowCamera);
          }
        }
        resource.revision = this.staticShadowRevision;
        resource.signature = signature;
        resource.strength = Math.min(1, resource.strength + delta / 0.35);
        this.localShadowInfo[slot * 4 + 1] = resource.strength;
        this.localShadowInfo[slot * 4 + 2] = 1 / resource.size;
        this.localShadowTextures[slot * 2] = resource.textures[0];
        this.localShadowTextures[slot * 2 + 1] = resource.textures[1];
      }
    } finally {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, this.canvas.width, this.canvas.height);
      gl.disable(gl.POLYGON_OFFSET_FILL); gl.bindVertexArray(null);
      this.shadowActive = false; this.localShadowActive = false;
      this.currentShadowCamera = this.shadowCamera;
    }
    const bytes = this.localShadowResources.size * budget.localShadowSize ** 2 * 6 * 4 * 2;
    this.metrics.textureBytes += bytes - this.metrics.localShadowTextureBytes;
    this.metrics.localShadowTextureBytes = bytes;
    this.metrics.localShadowMapCount = this.localShadowResources.size;
    this.metrics.localShadowMapSize = budget.localShadowSize;
    this.metrics.localShadowCpuMs = performance.now() - started;
  }

  beginShadowPass(focus: readonly [number, number, number]): DirectionalShadowCamera | null {
    this.metrics.shadowDrawCalls = 0;
    this.metrics.shadowTriangles = 0;
    this.metrics.shadowCpuMs = 0;
    this.metrics.instanceBufferAllocatedBytesThisFrame = 0;
    this.shadowReady = false;
    if (!this.options.advancedLighting || !this.atmosphereFlags.lighting) return null;
    // Select before actor pose preparation so off-camera local-light casters
    // enter the same once-per-frame preparation union even at night.
    this.localLightSelection.update(this.localLights, focus, this.lightingQuality, this.frameTimeSeconds);
    this.localShadowSelection.update(this.localLightSelection.selected, focus, this.lightingQuality);
    this.shadowStartedAt = performance.now();
    this.ensureShadowResources();
    const resources = this.shadowResources!;
    if (this.environment && this.environment.sunShadowStrength <= 0) return null;
    this.shadowCamera.updateFocus(focus, resources.size, this.environment?.sunDirection);
    this.currentShadowCamera = this.shadowCamera;
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, resources.framebuffer);
    gl.viewport(0, 0, resources.size, resources.size);
    gl.enable(gl.DEPTH_TEST);
    gl.depthMask(true);
    gl.disable(gl.BLEND);
    gl.enable(gl.CULL_FACE);
    gl.cullFace(gl.BACK);
    gl.enable(gl.POLYGON_OFFSET_FILL);
    gl.polygonOffset(1.1, 2);
    gl.clear(gl.DEPTH_BUFFER_BIT);
    this.shadowActive = true;
    return this.shadowCamera;
  }

  endShadowPass(): void {
    if (!this.shadowActive) return;
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.disable(gl.POLYGON_OFFSET_FILL);
    gl.bindVertexArray(null);
    this.shadowActive = false;
    this.shadowReady = true;
    this.metrics.shadowCpuMs = performance.now() - this.shadowStartedAt;
  }

  recordShadowDraw(triangles: number): void {
    if (this.localShadowActive) {
      this.metrics.localShadowDrawCalls += 1;
      this.metrics.localShadowTriangles += triangles;
      this.localPassTriangles += triangles;
    }
    this.metrics.shadowDrawCalls += 1;
    this.metrics.shadowTriangles += triangles;
  }

  bindLightingUniforms(uniforms: LightingUniforms): void {
    if (!this.options.advancedLighting) return;
    const gl = this.gl;
    gl.uniformMatrix4fv(uniforms.shadowMatrix, false, this.shadowCamera.viewProjection);
    gl.uniform1i(uniforms.shadowMap, LIGHTING_CONFIG.shadowTextureUnit);
    gl.uniform1f(uniforms.shadowTexel, 1 / (this.shadowResources?.size ?? 1));
    gl.uniform1f(uniforms.shadowEnabled, this.shadowReady && this.atmosphereFlags.lighting ? (this.environment?.sunShadowStrength ?? 1) : 0);
    gl.activeTexture(gl.TEXTURE0 + LIGHTING_CONFIG.shadowTextureUnit);
    gl.bindTexture(gl.TEXTURE_2D, this.shadowResources?.texture ?? this.whiteTexture);
    gl.activeTexture(gl.TEXTURE0);
    gl.uniform1i(uniforms.lightCount, this.atmosphereFlags.lighting ? this.localLightSelection.selected.length : 0);
    gl.uniform4fv(uniforms.positionRadius, this.localLightSelection.positionRadius);
    gl.uniform4fv(uniforms.colorIntensity, this.localLightSelection.colorIntensity);
    gl.uniform4fv(uniforms.directionOuter, this.localLightSelection.directionOuter);
    gl.uniform1fv(uniforms.innerCone, this.localLightSelection.innerCone);
    gl.uniform1fv(uniforms.distanceFalloff, this.localLightSelection.distanceFalloff);
    gl.uniform1fv(uniforms.photometric, this.localLightSelection.photometric);
    gl.uniform3f(uniforms.ambientColor, ...(this.environment?.ambientColor ?? this.biomeAtmosphere.ambientColor));
    gl.uniform3f(uniforms.sunDirection, ...(this.environment?.sunDirection ?? ATMOSPHERE_CONFIG.sunDirection));
    gl.uniform3f(uniforms.sunColor, ...(this.environment?.sunColor ?? ATMOSPHERE_CONFIG.sunColor));
    this.localShadowInfo[0] = this.localLightSelection.selected.findIndex((light) => light.id === this.localShadowSelection.selected[0]?.id);
    this.localShadowInfo[4] = this.localLightSelection.selected.findIndex((light) => light.id === this.localShadowSelection.selected[1]?.id);
    gl.uniform4fv(uniforms.localInfo, this.localShadowInfo);
    gl.uniformMatrix4fv(uniforms.localMatrices, false, this.localShadowMatrices);
    for (let index = 0; index < 4; index += 1) {
      gl.uniform1i(uniforms.localSamplers[index]!, 3 + index);
      gl.activeTexture(gl.TEXTURE0 + 3 + index);
      gl.bindTexture(gl.TEXTURE_2D, this.localShadowTextures[index] ?? this.whiteTexture);
    }
    gl.activeTexture(gl.TEXTURE0);
  }

  lightingSnapshot() {
    return {
      quality: this.lightingQuality,
      shadow: {
        enabled: this.shadowReady && this.atmosphereFlags.lighting,
        size: this.metrics.shadowMapSize,
        extent: LIGHTING_CONFIG.shadowHalfExtent * 2,
        textureBytes: this.metrics.shadowTextureBytes,
        drawCalls: this.metrics.shadowDrawCalls - this.metrics.localShadowDrawCalls,
        triangles: this.metrics.shadowTriangles - this.metrics.localShadowTriangles,
        cpuMs: this.metrics.shadowCpuMs,
      },
      sun: this.environment ? { direction: this.environment.sunDirection, color: this.environment.sunColor, shadowStrength: this.environment.sunShadowStrength } : null,
      localShadows: { limit: LIGHTING_CONFIG.quality[this.lightingQuality].localShadowLights,
        triangleBudgetPerFace: LIGHTING_CONFIG.quality[this.lightingQuality].localShadowTriangles / 6,
        count: this.metrics.localShadowMapCount, size: this.metrics.localShadowMapSize,
        textureBytes: this.metrics.localShadowTextureBytes, cpuMs: this.metrics.localShadowCpuMs,
        drawCalls: this.metrics.localShadowDrawCalls, triangles: this.metrics.localShadowTriangles,
        cacheHits: this.metrics.localShadowCacheHits, lights: this.localShadowSelection.selected.map((light) => light.id) },
      localLightLimit: LIGHTING_CONFIG.quality[this.lightingQuality].localLights,
      localLightCount: this.localLightSelection.selected.length,
      localLights: this.localLightSelection.selected.map((light) => ({ ...light })),
    };
  }

  disposeLighting(): void {
    this.disposeDirectionalLighting();
    for (const resource of this.localShadowResources.values()) destroyLocalShadow(this.gl, resource);
    this.localShadowResources.clear();
    this.localShadowTextures.length = 0; this.localShadowInfo.fill(0);
    this.metrics.textureBytes -= this.metrics.localShadowTextureBytes;
    this.metrics.localShadowTextureBytes = 0; this.metrics.localShadowMapCount = 0;
  }

  private disposeDirectionalLighting(): void {
    const resources = this.shadowResources;
    if (!resources) return;
    const gl = this.gl;
    gl.deleteFramebuffer(resources.framebuffer);
    gl.deleteTexture(resources.texture);
    gl.deleteProgram(resources.program);
    this.metrics.textureBytes -= this.metrics.shadowTextureBytes;
    this.metrics.shadowTextureBytes = 0;
    this.metrics.shadowMapSize = 0;
    this.shadowResources = undefined;
    this.shadowReady = false;
  }

  private ensureShadowResources(): void {
    const size = LIGHTING_CONFIG.quality[this.lightingQuality].shadowSize;
    if (this.shadowResources?.size === size) return;
    this.disposeDirectionalLighting();
    const gl = this.gl;
    const texture = gl.createTexture();
    const framebuffer = gl.createFramebuffer();
    if (!texture || !framebuffer) throw new Error("Unable to allocate directional shadow resources");
    gl.activeTexture(gl.TEXTURE0 + LIGHTING_CONFIG.shadowTextureUnit);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.DEPTH_COMPONENT24, size, size, 0, gl.DEPTH_COMPONENT, gl.UNSIGNED_INT, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, texture, 0);
    gl.drawBuffers([gl.NONE]);
    gl.readBuffer(gl.NONE);
    const complete = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.activeTexture(gl.TEXTURE0);
    if (!complete) {
      gl.deleteTexture(texture);
      gl.deleteFramebuffer(framebuffer);
      throw new Error("Directional shadow framebuffer is incomplete");
    }
    const program = createProgram(gl, vertexSource, shadowFragmentSource);
    const uniform = (name: string) => gl.getUniformLocation(program, name);
    this.shadowResources = { size, texture, framebuffer, program, uniforms: {
      viewProjection: uniform("uViewProjection"), model: uniform("uModel"), tint: uniform("uTint"),
      instanced: uniform("uInstanced"), textured: uniform("uTextured"), alphaCutoff: uniform("uAlphaCutoff"),
      baseColorTexture: uniform("uBaseColorTexture"),
    } };
    this.metrics.shadowMapSize = size;
    this.metrics.shadowTextureBytes = size * size * 4;
    this.metrics.textureBytes += this.metrics.shadowTextureBytes;
  }

  get tpsAtmosphereEnabled(): boolean { return this.tpsAtmosphere; }

  get fogProfile(): AtmosphereFogProfile {
    const base = this.tpsAtmosphere ? (this.environment ?? this.tpsFog) : (this.legacyEnvironmentFog ?? this.biomeAtmosphere);
    const amount = this.underwaterAmount;
    if (amount <= 0) return base;
    const profile = this.underwaterFog;
    profile.fogColor[0] = base.fogColor[0] + (0.105 - base.fogColor[0]) * amount;
    profile.fogColor[1] = base.fogColor[1] + (0.20 - base.fogColor[1]) * amount;
    profile.fogColor[2] = base.fogColor[2] + (0.21 - base.fogColor[2]) * amount;
    profile.fogNear = base.fogNear + (1.4 - base.fogNear) * amount;
    profile.fogFar = base.fogFar + (18 - base.fogFar) * amount;
    profile.fogMaximum = base.fogMaximum + (1 - base.fogMaximum) * amount;
    return profile;
  }

  /** The caller smooths this from the stable mean surface, never from visual waves. */
  setUnderwaterAmount(amount: number): void { this.underwaterAmount = unitInterval(amount); }

  updateWaterActor(id: string, x: number, z: number, surfaceY: number, vx: number, vz: number, locomotion: WaterVisualLocomotion, timeSeconds: number): void {
    const started = performance.now();
    this.waterEffects.updateActor(id, x, z, surfaceY, vx, vz, locomotion, timeSeconds);
    this.waterUpdateCpuMs += performance.now() - started;
  }

  finishWaterEffects(timeSeconds: number, focusX: number, focusZ: number): void {
    const started = performance.now();
    this.waterEffects.finish(timeSeconds, focusX, focusZ, this.lightingQuality);
    this.metrics.waterUpdateCpuMs = this.waterUpdateCpuMs + performance.now() - started;
    this.metrics.rippleActiveCount = this.waterEffects.rippleCount;
    this.metrics.splashActiveCount = this.waterEffects.splashCount;
    this.waterUpdateCpuMs = 0;
  }

  clearWaterEffects(): void {
    this.waterEffects.clear();
    this.metrics.rippleActiveCount = 0;
    this.metrics.splashActiveCount = 0;
  }

  waterSnapshot() {
    return {
      ...WATER_VISUAL_CONFIG.quality[this.lightingQuality],
      maximumWaveDisplacement: WATER_VISUAL_CONFIG.waves.reduce((sum, wave) => sum + wave[0], 0),
      maximumRipples: WATER_VISUAL_CONFIG.maximumRipples,
      activeRipples: this.waterEffects.rippleCount,
      activeSplashes: this.waterEffects.splashCount,
      underwaterAmount: this.underwaterAmount,
      updateCpuMs: this.metrics.waterUpdateCpuMs,
    };
  }

  setTpsAtmosphere(enabled: boolean): void {
    if (enabled && !this.sky) {
      const program = createProgram(this.gl, skyVertexSource, skyFragmentSource);
      const uniform = (name: string): WebGLUniformLocation => {
        const location = this.gl.getUniformLocation(program, name);
        if (location === null) throw new Error(`Missing sky shader uniform ${name}`);
        return location;
      };
      this.sky = { program, viewProjection: uniform("uViewProjection"), fogColor: uniform("uFogColor") };
    }
    this.tpsAtmosphere = enabled;
  }

  setClearColor(color: readonly [number, number, number] | null): void {
    this.clearColor = color ? [unitInterval(color[0]), unitInterval(color[1]), unitInterval(color[2])] : null;
  }

  setAiGlowEnabled(enabled: boolean): void {
    this.aiGlowEnabled = enabled;
  }

  setBiomeAtmosphere(primary: BiomeId, secondary: BiomeId | null, secondaryWeight: number): void {
    this.biomeAtmosphere = blendBiomeAtmosphere(primary, secondary, secondaryWeight);
    this.tpsFog = tpsFogProfile(this.biomeAtmosphere);
    if (this.environment) this.legacyEnvironmentFog = legacyEnvironmentFogProfile(this.environment, this.biomeAtmosphere);
  }

  aiGlowSnapshot(): { enabled: boolean } {
    return { enabled: this.aiGlowEnabled };
  }

  atmosphereSnapshot(): {
    flags: AtmosphereFeatureFlags;
    fogNear: number;
    fogFar: number;
    fogMaximum: number;
    fogColor: readonly [number, number, number];
    tps: boolean;
    sky: "overcast-gradient" | "dynamic-environment" | "none";
    particleLimit: number;
    shadows: "none" | "directional-near";
  } {
    const fog = this.fogProfile;
    return {
      flags: { ...this.atmosphereFlags },
      fogNear: fog.fogNear,
      fogFar: fog.fogFar,
      fogMaximum: fog.fogMaximum,
      fogColor: fog.fogColor,
      tps: this.tpsAtmosphere,
      sky: this.tpsAtmosphere ? (this.environment ? "dynamic-environment" : "overcast-gradient") : "none",
      particleLimit: this.effectiveParticleCount(),
      shadows: this.shadowReady && this.atmosphereFlags.lighting ? "directional-near" : "none",
    };
  }

  get viewportWidth(): number { return this.cssWidth; }

  get viewportHeight(): number { return this.cssHeight; }

  get aspect(): number { return this.cssWidth / this.cssHeight; }

  resize(cssWidth = this.canvas.clientWidth, cssHeight = this.canvas.clientHeight): void {
    this.cssWidth = Math.max(1, cssWidth);
    this.cssHeight = Math.max(1, cssHeight);
    this.applyResolution();
  }

  private applyResolution(): void {
    const { width, height } = renderResolution(
      this.cssWidth, this.cssHeight, this.resolutionScale, this.maximumWidth, this.options.responsiveResolution,
    );
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
    this.metrics.width = width;
    this.metrics.height = height;
  }

  begin(
    viewProjection: Mat4,
    camera: readonly number[],
    target: readonly number[],
    timeSeconds = performance.now() / 1_000,
  ): void {
    this.frameStartedAt = performance.now();
    this.metrics.drawCalls = this.metrics.shadowDrawCalls;
    this.metrics.triangles = this.metrics.shadowTriangles;
    this.metrics.visibleTerrainChunks = 0;
    this.metrics.waterDrawCalls = 0;
    this.metrics.waterTriangles = 0;
    this.metrics.waterSplashDrawCalls = 0;
    this.waterFrameBound = false;
    this.frameTimeSeconds = timeSeconds;
    this.frameViewProjection = viewProjection;
    this.frameCamera[0] = camera[0]!;
    this.frameCamera[1] = camera[1]!;
    this.frameCamera[2] = camera[2]!;
    this.metrics.geologyDrawCalls = 0;
    this.metrics.geologyTriangles = 0;
    this.metrics.visibleGeologyInstances = 0;
    this.metrics.vegetationDrawCalls = 0;
    this.metrics.vegetationTriangles = 0;
    this.metrics.visibleVegetationTrees = 0;
    this.metrics.visibleVegetationShrubs = 0;
    this.metrics.humanDrawCalls = 0;
    this.metrics.humanTriangles = 0;
    this.metrics.visibleHumanObjects = 0;
    this.metrics.itemDrawCalls = 0;
    this.metrics.itemTriangles = 0;
    this.metrics.visibleItemEntities = 0;
    this.metrics.aiDrawCalls = 0;
    this.metrics.aiTriangles = 0;
    this.metrics.visibleAiObjects = 0;
    this.metrics.characterDrawCalls = 0;
    this.metrics.characterTriangles = 0;
    this.metrics.visibleCharacters = 0;
    this.metrics.totalCharacters = 0;
    this.metrics.characterJointUpdates = 0;
    this.metrics.characterAnimationCpuMs = 0;
    this.metrics.atmosphereDrawCalls = 0;
    this.metrics.particleDrawCalls = 0;
    this.metrics.particleCount = 0;
    this.metrics.contactShadowDrawCalls = 0;
    this.metrics.contactShadowActors = 0;
    this.metrics.contactShadowTriangles = 0;
    if (!this.shadowReady) this.metrics.instanceBufferAllocatedBytesThisFrame = 0;
    const gl = this.gl;
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    const clearColor = this.clearColor ?? this.fogProfile.fogColor;
    gl.clearColor(clearColor[0], clearColor[1], clearColor[2], 1);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    if (this.tpsAtmosphere) this.drawSky(viewProjection);
    gl.useProgram(this.program);
    gl.uniformMatrix4fv(this.uniforms.viewProjection, false, viewProjection);
    gl.uniform3f(this.uniforms.camera, camera[0]!, camera[1]!, camera[2]!);
    const forwardX = target[0]! - camera[0]!;
    const forwardY = target[1]! - camera[1]!;
    const forwardZ = target[2]! - camera[2]!;
    const forwardLength = Math.hypot(forwardX, forwardY, forwardZ) || 1;
    this.viewForward[0] = forwardX / forwardLength;
    this.viewForward[1] = forwardY / forwardLength;
    this.viewForward[2] = forwardZ / forwardLength;
    gl.uniform3f(this.uniforms.viewForward, this.viewForward[0], this.viewForward[1], this.viewForward[2]);
    gl.uniform3f(this.uniforms.sunDirection, ...(this.environment?.sunDirection ?? ATMOSPHERE_CONFIG.sunDirection));
    gl.uniform3f(this.uniforms.ambientColor, ...(this.environment?.ambientColor ?? this.biomeAtmosphere.ambientColor));
    gl.uniform3f(this.uniforms.sunColor, ...(this.environment?.sunColor ?? ATMOSPHERE_CONFIG.sunColor));
    const fog = this.fogProfile;
    gl.uniform3f(this.uniforms.fogColor, ...fog.fogColor);
    gl.uniform1f(this.uniforms.fogNear, fog.fogNear);
    gl.uniform1f(this.uniforms.fogFar, fog.fogFar);
    gl.uniform1f(this.uniforms.fogMaximum, fog.fogMaximum);
    gl.uniform1f(this.uniforms.tpsAtmosphere, this.tpsAtmosphere ? 1 : 0);
    gl.uniform1f(this.uniforms.lightingEnabled, this.atmosphereFlags.lighting ? 1 : 0);
    gl.uniform1f(this.uniforms.fogEnabled, this.atmosphereFlags.fog ? 1 : 0);
    gl.uniform1f(this.uniforms.gradingEnabled, this.atmosphereFlags.grading ? 1 : 0);
    gl.uniform1f(this.uniforms.aiGlowEnabled, this.aiGlowEnabled ? 1 : 0);
    gl.uniform1f(this.uniforms.time, timeSeconds);
    gl.uniform1i(this.uniforms.baseColorTexture, 0);
    this.bindEnvironmentUniforms(this.program);
    if (this.lightingUniforms) {
      const localLightStartedAt = performance.now();
      this.localLightSelection.update(this.localLights, target, this.lightingQuality, timeSeconds);
      this.metrics.localLightSelectionCpuMs = performance.now() - localLightStartedAt;
      this.metrics.selectedLocalLights = this.localLightSelection.selected.length;
      this.bindLightingUniforms(this.lightingUniforms);
    }
  }

  drawAtmosphereParticles(viewProjection: Mat4, camera: readonly number[], center: readonly number[], timeSeconds: number): void {
    const particleCount = this.effectiveParticleCount();
    if (!this.atmosphereFlags.particles || particleCount <= 0) return;
    const gl = this.gl;
    gl.useProgram(this.particleProgram);
    gl.bindVertexArray(this.particleVertexArray);
    gl.uniformMatrix4fv(this.particleUniforms.viewProjection, false, viewProjection);
    gl.uniform3f(this.particleUniforms.center, center[0]!, center[1]!, center[2]!);
    gl.uniform3f(this.particleUniforms.camera, camera[0]!, camera[1]!, camera[2]!);
    gl.uniform3f(this.particleUniforms.viewForward, this.viewForward[0], this.viewForward[1], this.viewForward[2]);
    gl.uniform1f(this.particleUniforms.time, timeSeconds);
    gl.uniform1f(this.particleUniforms.pointScale, Math.max(0.75, this.canvas.height / 390));
    const fog = this.fogProfile;
    gl.uniform1f(this.particleUniforms.fogNear, fog.fogNear);
    gl.uniform1f(this.particleUniforms.fogFar, fog.fogFar);
    gl.uniform1f(this.particleUniforms.fogMaximum, fog.fogMaximum);
    gl.uniform1f(this.particleUniforms.fogEnabled, this.atmosphereFlags.fog ? 1 : 0);
    gl.uniform1f(this.particleUniforms.tpsAtmosphere, this.tpsAtmosphere ? 1 : 0);
    gl.uniform1f(this.particleUniforms.dynamicParticles, this.environment ? 1 : 0);
    gl.uniform2f(this.particleUniforms.ambientWind,
      this.environment ? Math.cos(this.environment.windDirectionRadians) * this.environment.windSpeed * 1.2 : 0,
      this.environment ? Math.sin(this.environment.windDirectionRadians) * this.environment.windSpeed * 1.2 : 0);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.depthMask(false);
    gl.drawArrays(gl.POINTS, 0, particleCount);
    gl.depthMask(true);
    gl.disable(gl.BLEND);
    this.metrics.drawCalls += 1;
    this.metrics.atmosphereDrawCalls += 1;
    this.metrics.particleDrawCalls = 1;
    this.metrics.particleCount = particleCount;
  }

  private drawSky(viewProjection: Mat4): void {
    if (!this.sky) return;
    const gl = this.gl;
    gl.useProgram(this.sky.program);
    gl.bindVertexArray(null);
    gl.uniformMatrix4fv(this.sky.viewProjection, false, viewProjection);
    gl.uniform3f(this.sky.fogColor, ...this.fogProfile.fogColor);
    this.bindEnvironmentUniforms(this.sky.program);
    gl.disable(gl.DEPTH_TEST);
    gl.depthMask(false);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.depthMask(true);
    gl.enable(gl.DEPTH_TEST);
    this.metrics.drawCalls += 1;
    this.metrics.triangles += 1;
    this.metrics.atmosphereDrawCalls += 1;
  }

  drawTerrain(mesh: GpuMesh): void {
    this.draw(mesh, this.identity, [1, 1, 1, 1], 0);
    this.metrics.visibleTerrainChunks += 1;
  }

  drawStudioTerrain(mesh: GpuMesh, biomeMaskVisible: boolean): void {
    this.draw(mesh, this.identity, [1, 1, 1, 1], biomeMaskVisible ? -2 : -1);
    this.metrics.visibleTerrainChunks += 1;
  }

  drawMarker(mesh: GpuMesh, matrix: Mat4): void {
    this.draw(mesh, matrix, [0.12, 0.105, 0.09, 1], 1);
  }

  drawEditorOverlay(mesh: GpuMesh, tint: readonly [number, number, number, number]): void {
    this.draw(mesh, this.identity, tint, 1);
  }

  drawWater(mesh: GpuMesh): void {
    if (this.shadowActive) return;
    // Studio retains its existing isolated preview path.
    if (!this.options.advancedLighting) this.draw(mesh, this.identity, [1, 1, 1, 1], 2);
    else {
      const water = this.ensureWaterResources();
      const gl = this.gl;
      gl.useProgram(water.program);
      if (!this.waterFrameBound) {
        const uniforms = water.uniforms;
        const fog = this.fogProfile;
        const quality = WATER_VISUAL_CONFIG.quality[this.lightingQuality];
        gl.uniformMatrix4fv(uniforms.uViewProjection!, false, this.frameViewProjection);
        gl.uniform3f(uniforms.uCamera!, ...this.frameCamera);
        gl.uniform3f(uniforms.uViewForward!, ...this.viewForward);
        gl.uniform3f(uniforms.uSunDirection!, ...(this.environment?.sunDirection ?? ATMOSPHERE_CONFIG.sunDirection));
        gl.uniform3f(uniforms.uSunColor!, ...(this.environment?.sunColor ?? ATMOSPHERE_CONFIG.sunColor));
        gl.uniform3f(uniforms.uFogColor!, ...fog.fogColor);
        gl.uniform1f(uniforms.uFogNear!, fog.fogNear);
        gl.uniform1f(uniforms.uFogFar!, fog.fogFar);
        gl.uniform1f(uniforms.uFogMaximum!, fog.fogMaximum);
        gl.uniform1f(uniforms.uTpsAtmosphere!, this.tpsAtmosphere ? 1 : 0);
        gl.uniform1f(uniforms.uFogEnabled!, this.atmosphereFlags.fog ? 1 : 0);
        gl.uniform1f(uniforms.uLightingEnabled!, this.atmosphereFlags.lighting ? 1 : 0);
        gl.uniform1f(uniforms.uTime!, this.frameTimeSeconds);
        gl.uniform1i(uniforms.uWaveCount!, quality.waves);
        gl.uniform1f(uniforms.uLocalSpecular!, quality.localSpecular ? 1 : 0);
        gl.uniform1i(uniforms.uRippleCount!, this.waterEffects.rippleCount);
        gl.uniform4fv(uniforms["uRipples[0]"]!, this.waterEffects.packedRipples);
        this.bindLightingUniforms(water.lighting);
        this.bindEnvironmentUniforms(water.program);
        this.waterFrameBound = true;
      }
      gl.bindVertexArray(mesh.vertexArray);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.disable(gl.CULL_FACE);
      gl.drawElements(gl.TRIANGLES, mesh.indexCount, gl.UNSIGNED_SHORT, 0);
      gl.enable(gl.CULL_FACE);
      gl.disable(gl.BLEND);
      this.metrics.drawCalls += 1;
      this.metrics.triangles += mesh.triangleCount;
    }
    this.metrics.waterDrawCalls += 1;
    this.metrics.waterTriangles += mesh.triangleCount;
  }

  drawWaterEffects(): void {
    if (this.shadowActive || this.waterEffects.splashCount <= 0 || !this.options.advancedLighting) return;
    const water = this.ensureWaterResources();
    const gl = this.gl;
    const uniforms = water.splashUniforms;
    const fog = this.fogProfile;
    gl.useProgram(water.splashProgram);
    gl.bindVertexArray(null);
    gl.uniformMatrix4fv(uniforms.uViewProjection!, false, this.frameViewProjection);
    gl.uniform4fv(uniforms["uSplashes[0]"]!, this.waterEffects.splashData);
    gl.uniform1fv(uniforms["uSplashStrength[0]"]!, this.waterEffects.splashStrength);
    gl.uniform1f(uniforms.uTime!, this.frameTimeSeconds);
    gl.uniform1f(uniforms.uPointScale!, Math.max(0.75, this.canvas.height / 390));
    gl.uniform3f(uniforms.uCamera!, ...this.frameCamera);
    gl.uniform1f(uniforms.uFogNear!, fog.fogNear);
    gl.uniform1f(uniforms.uFogFar!, fog.fogFar);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.depthMask(false);
    gl.drawArrays(gl.POINTS, 0, WATER_VISUAL_CONFIG.maximumSplashes * WATER_VISUAL_CONFIG.dropletsPerSplash);
    gl.depthMask(true);
    gl.disable(gl.BLEND);
    this.metrics.drawCalls += 1;
    this.metrics.waterSplashDrawCalls += 1;
  }

  disposeWater(): void {
    if (!this.waterResources) return;
    this.gl.deleteProgram(this.waterResources.program);
    this.gl.deleteProgram(this.waterResources.splashProgram);
    this.waterResources = undefined;
    this.waterFrameBound = false;
    this.clearWaterEffects();
  }

  private ensureWaterResources() {
    if (this.waterResources) return this.waterResources;
    const gl = this.gl;
    const program = createProgram(gl, WATER_VERTEX_SOURCE, WATER_FRAGMENT_SOURCE);
    const splashProgram = createProgram(gl, WATER_SPLASH_VERTEX_SOURCE, WATER_SPLASH_FRAGMENT_SOURCE);
    const locations = (shader: WebGLProgram, names: readonly string[]) => Object.fromEntries(names.map((name) => [name, gl.getUniformLocation(shader, name)]));
    this.waterResources = {
      program,
      lighting: createLightingUniforms(gl, program),
      uniforms: locations(program, ["uViewProjection", "uCamera", "uViewForward", "uSunDirection", "uSunColor", "uFogColor", "uFogNear", "uFogFar", "uFogMaximum", "uTpsAtmosphere", "uFogEnabled", "uLightingEnabled", "uTime", "uWaveCount", "uLocalSpecular", "uRippleCount", "uRipples[0]"]),
      splashProgram,
      splashUniforms: locations(splashProgram, ["uViewProjection", "uSplashes[0]", "uSplashStrength[0]", "uTime", "uPointScale", "uCamera", "uFogNear", "uFogFar"]),
    };
    return this.waterResources;
  }

  drawGeologyInstanced(mesh: GpuMesh, data: Float32Array, instanceCount: number): void {
    this.drawStaticInstanced(mesh, data, instanceCount, { alphaCutoff: 0, doubleSided: false }, "geology");
  }

  drawStaticInstanced(
    mesh: GpuMesh,
    data: Float32Array,
    instanceCount: number,
    material: StaticRenderMaterial,
    kind: StaticRenderKind,
  ): void {
    if (instanceCount <= 0 || data.length < instanceCount * 20) return;
    if (this.shadowActive) {
      const remaining = this.remainingShadowTriangles;
      instanceCount = Math.min(instanceCount, Math.floor(remaining / Math.max(1, mesh.triangleCount)));
      if (instanceCount <= 0) return;
    }
    const gl = this.gl;
    const allocated = mesh.uploadInstances(gl, data, instanceCount);
    this.meshMemoryBytes += allocated;
    this.metrics.instanceBufferBytes += allocated;
    this.metrics.instanceBufferAllocatedBytesThisFrame += allocated;
    if (allocated > 0) this.metrics.instanceBufferGrowths += 1;
    if (kind === "geology") this.metrics.geologyBufferBytes += allocated;
    else if (kind === "vegetation") this.metrics.vegetationBufferBytes += allocated;
    else if (kind === "human") this.metrics.humanBufferBytes += allocated;
    else if (kind === "item") this.metrics.itemBufferBytes += allocated;
    else this.metrics.aiBufferBytes += allocated;
    if (this.shadowActive) {
      this.drawShadowMesh(mesh, this.identity, [1, 1, 1, 1], instanceCount, material);
      return;
    }
    gl.useProgram(this.program);
    gl.bindVertexArray(mesh.vertexArray);
    gl.uniformMatrix4fv(this.uniforms.model, false, this.identity);
    gl.uniform4f(this.uniforms.tint, 1, 1, 1, 1);
    gl.uniform1f(this.uniforms.material, kind === "ai" || kind === "item" ? 4 : 3);
    gl.uniform1f(this.uniforms.instanced, 1);
    gl.uniform1f(this.uniforms.textured, material.texture ? 1 : 0);
    gl.uniform1f(this.uniforms.alphaCutoff, material.alphaCutoff);
    this.applyEmissiveMaterial(material, kind === "ai");
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, material.texture ?? this.whiteTexture);
    if (material.doubleSided) gl.disable(gl.CULL_FACE);
    gl.drawElementsInstanced(gl.TRIANGLES, mesh.indexCount, gl.UNSIGNED_SHORT, 0, instanceCount);
    if (material.doubleSided) gl.enable(gl.CULL_FACE);
    this.metrics.drawCalls += 1;
    if (kind === "geology") {
      this.metrics.geologyDrawCalls += 1;
      this.metrics.geologyTriangles += mesh.triangleCount * instanceCount;
    } else if (kind === "vegetation") {
      this.metrics.vegetationDrawCalls += 1;
      this.metrics.vegetationTriangles += mesh.triangleCount * instanceCount;
    } else if (kind === "human") {
      this.metrics.humanDrawCalls += 1;
      this.metrics.humanTriangles += mesh.triangleCount * instanceCount;
    } else if (kind === "ai") {
      this.metrics.aiDrawCalls += 1;
      this.metrics.aiTriangles += mesh.triangleCount * instanceCount;
    } else {
      this.metrics.itemDrawCalls += 1;
      this.metrics.itemTriangles += mesh.triangleCount * instanceCount;
    }
    this.metrics.triangles += mesh.triangleCount * instanceCount;
  }

  /** Discard the interval spanning an intentional pause, such as a hidden tab. */
  resetFrameTiming(): void {
    this.previousFrameAt = null;
  }

  end(): void {
    this.gl.bindVertexArray(null);
    const now = performance.now();
    const interval = this.previousFrameAt === null ? 0 : now - this.previousFrameAt;
    const cpu = now - this.frameStartedAt + this.metrics.shadowCpuMs + this.metrics.localShadowCpuMs;
    this.previousFrameAt = now;
    // Slow visible frames are the most important samples; excluding them would
    // report healthy FPS while software rendering is actually running at 3 Hz.
    if (interval > 0) this.smoothedFrameMs += (interval - this.smoothedFrameMs) * 0.08;
    this.smoothedCpuMs += (cpu - this.smoothedCpuMs) * 0.08;
    this.metrics.frameMs = this.smoothedFrameMs;
    this.metrics.cpuMs = this.smoothedCpuMs;
    this.metrics.fps = Math.min(999, 1_000 / Math.max(0.1, this.smoothedFrameMs));
  }

  private draw(
    mesh: GpuMesh,
    matrix: Mat4,
    tint: readonly [number, number, number, number],
    material: number,
  ): void {
    if (this.shadowActive) {
      if (mesh.triangleCount <= this.remainingShadowTriangles) this.drawShadowMesh(mesh, matrix, tint, 0);
      return;
    }
    const gl = this.gl;
    gl.useProgram(this.program);
    gl.bindVertexArray(mesh.vertexArray);
    gl.uniformMatrix4fv(this.uniforms.model, false, matrix);
    gl.uniform4f(this.uniforms.tint, tint[0], tint[1], tint[2], tint[3]);
    gl.uniform1f(this.uniforms.material, material);
    gl.uniform1f(this.uniforms.instanced, 0);
    gl.uniform1f(this.uniforms.textured, 0);
    gl.uniform1f(this.uniforms.alphaCutoff, 0);
    this.applyEmissiveMaterial();
    gl.drawElements(gl.TRIANGLES, mesh.indexCount, gl.UNSIGNED_SHORT, 0);
    this.metrics.drawCalls += 1;
    this.metrics.triangles += mesh.triangleCount;
  }

  private drawShadowMesh(mesh: GpuMesh, matrix: Mat4, tint: readonly [number, number, number, number], instances: number, material?: StaticRenderMaterial): void {
    const gl = this.gl;
    const shadow = this.shadowResources!;
    const uniforms = shadow.uniforms;
    gl.useProgram(shadow.program);
    gl.bindVertexArray(mesh.vertexArray);
    gl.uniformMatrix4fv(uniforms.viewProjection, false, this.currentShadowCamera.viewProjection);
    gl.uniformMatrix4fv(uniforms.model, false, matrix);
    gl.uniform4f(uniforms.tint, ...tint);
    gl.uniform1f(uniforms.instanced, instances > 0 ? 1 : 0);
    gl.uniform1f(uniforms.textured, material?.texture ? 1 : 0);
    gl.uniform1f(uniforms.alphaCutoff, material?.alphaCutoff ?? 0);
    gl.uniform1i(uniforms.baseColorTexture, 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, material?.texture ?? this.whiteTexture);
    if (material?.doubleSided) gl.disable(gl.CULL_FACE);
    if (instances > 0) gl.drawElementsInstanced(gl.TRIANGLES, mesh.indexCount, gl.UNSIGNED_SHORT, 0, instances);
    else gl.drawElements(gl.TRIANGLES, mesh.indexCount, gl.UNSIGNED_SHORT, 0);
    if (material?.doubleSided) gl.enable(gl.CULL_FACE);
    this.recordShadowDraw(mesh.triangleCount * Math.max(1, instances));
  }

  private applyEmissiveMaterial(material?: StaticRenderMaterial, aiMaterial = false): void {
    const color = material?.emissiveColor ?? NO_EMISSIVE;
    const gl = this.gl;
    gl.uniform1f(this.uniforms.aiGlowEnabled, !aiMaterial || this.aiGlowEnabled ? 1 : 0);
    gl.uniform3f(
      this.uniforms.emissiveColor,
      unitInterval(color[0]),
      unitInterval(color[1]),
      unitInterval(color[2]),
    );
    gl.uniform1f(this.uniforms.emissiveStrength, unitInterval(material?.emissiveStrength ?? 0));
    gl.uniform1f(this.uniforms.pulseStrength, unitInterval(material?.pulseStrength ?? 0));
  }

  private effectiveParticleCount(): number {
    return Math.max(0, Math.min(this.particleCount, Math.round(this.particleCount * this.biomeAtmosphere.particleRatio)));
  }

  private async createTexture(url: string): Promise<WebGLTexture> {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Texture request failed: ${url}`);
    const bitmap = await createImageBitmap(await response.blob(), {
      premultiplyAlpha: "none",
      colorSpaceConversion: "none",
    });
    const texture = this.gl.createTexture();
    if (!texture) throw new Error("WebGL texture allocation failed");
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 0);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, bitmap);
    const allocationBytes = bitmap.width * bitmap.height * 4;
    this.textureAllocationBytes.set(texture, allocationBytes);
    this.metrics.textureBytes += allocationBytes;
    this.staticShadowRevision += 1;
    bitmap.close();
    return texture;
  }
}

function unitInterval(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0;
}

function createProgram(gl: WebGL2RenderingContext, vertex: string, fragment: string): WebGLProgram {
  const program = gl.createProgram();
  if (!program) throw new Error("Unable to create shader program");
  const compile = (type: number, source: string): WebGLShader => {
    const shader = gl.createShader(type);
    if (!shader) throw new Error("Unable to create shader");
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader) ?? "Shader error");
    gl.attachShader(program, shader);
    return shader;
  };
  const vertexShader = compile(gl.VERTEX_SHADER, vertex);
  const fragmentShader = compile(gl.FRAGMENT_SHADER, fragment);
  gl.linkProgram(program);
  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program) ?? "Link error");
  return program;
}
